/**
 * File icon utilities for displaying appropriate icons based on file type/extension
 */

/**
 * Returns the Font Awesome icon class for a given file type
 * @param fileType - MIME type or file type string (optional)
 * @param fileName - File name with extension (optional, used for more accurate detection)
 * @returns Font Awesome icon class string
 */
export const getFileIcon = (fileType: string, fileName?: string): string => {
  // Priority: 1) File extension, 2) MIME type fallback
  if (!fileType && !fileName) return "fas fa-file";

  const lowerFileType = fileType?.toLowerCase() || "";
  const lowerFileName = fileName?.toLowerCase() || "";

  // Check by extension first (more reliable)
  if (lowerFileName.endsWith(".pdf")) return "fas fa-file-pdf";
  if (lowerFileName.endsWith(".txt")) return "fas fa-file-lines";
  if (lowerFileName.endsWith(".csv")) return "fas fa-file-csv";
  if (lowerFileName.endsWith(".doc") || lowerFileName.endsWith(".docx"))
    return "fas fa-file-word";
  if (lowerFileName.endsWith(".xls") || lowerFileName.endsWith(".xlsx"))
    return "fas fa-file-excel";
  if (lowerFileName.endsWith(".ppt") || lowerFileName.endsWith(".pptx"))
    return "fas fa-file-powerpoint";
  if (
    lowerFileName.endsWith(".xml") ||
    lowerFileName.endsWith(".html") ||
    lowerFileName.endsWith(".css") ||
    lowerFileName.endsWith(".js") ||
    lowerFileName.endsWith(".json")
  )
    return "fas fa-file-code";
  if (
    lowerFileName.endsWith(".zip") ||
    lowerFileName.endsWith(".rar") ||
    lowerFileName.endsWith(".7z")
  )
    return "fas fa-file-zipper";
  if (lowerFileName.match(/\.(jpg|jpeg|png|gif|bmp|svg|webp)$/))
    return "fas fa-file-image";
  if (lowerFileName.match(/\.(mp3|wav|ogg|m4a|aac|flac)$/))
    return "fas fa-file-audio";
  if (lowerFileName.match(/\.(mp4|avi|mkv|mov|wmv|flv|webm)$/))
    return "fas fa-file-video";

  // Fallback to MIME type
  if (lowerFileType.includes("pdf")) return "fas fa-file-pdf";
  if (
    lowerFileType.includes("csv") ||
    lowerFileType.includes("comma-separated")
  )
    return "fas fa-file-csv";
  if (lowerFileType.includes("text") || lowerFileType.includes("plain"))
    return "fas fa-file-lines";
  if (
    lowerFileType.includes("word") ||
    lowerFileType.includes("document") ||
    lowerFileType.includes("msword")
  )
    return "fas fa-file-word";
  if (
    lowerFileType.includes("excel") ||
    lowerFileType.includes("sheet") ||
    lowerFileType.includes("spreadsheet")
  )
    return "fas fa-file-excel";
  if (
    lowerFileType.includes("powerpoint") ||
    lowerFileType.includes("presentation")
  )
    return "fas fa-file-powerpoint";
  if (
    lowerFileType.includes("xml") ||
    lowerFileType.includes("html") ||
    lowerFileType.includes("json") ||
    lowerFileType.includes("javascript")
  )
    return "fas fa-file-code";
  if (
    lowerFileType.includes("zip") ||
    lowerFileType.includes("rar") ||
    lowerFileType.includes("archive")
  )
    return "fas fa-file-zipper";
  if (lowerFileType.includes("image")) return "fas fa-file-image";
  if (lowerFileType.includes("audio")) return "fas fa-file-audio";
  if (lowerFileType.includes("video")) return "fas fa-file-video";

  return "fas fa-file";
};

/**
 * Returns the color hex code for a given file type icon
 * @param fileType - MIME type or file type string (optional)
 * @param fileName - File name with extension (optional, used for more accurate detection)
 * @returns Hex color string
 */
export const getFileIconColor = (
  fileType: string,
  fileName?: string,
): string => {
  if (!fileType && !fileName) return "#6c757d";

  const lowerFileType = fileType?.toLowerCase() || "";
  const lowerFileName = fileName?.toLowerCase() || "";

  // Check by extension first (more reliable)
  if (lowerFileName.endsWith(".pdf")) return "#e74c3c";
  if (lowerFileName.endsWith(".txt")) return "#6c757d";
  if (lowerFileName.endsWith(".csv")) return "#17a2b8"; // Cyan blue for CSV
  if (lowerFileName.endsWith(".doc") || lowerFileName.endsWith(".docx"))
    return "#2980b9";
  if (lowerFileName.endsWith(".xls") || lowerFileName.endsWith(".xlsx"))
    return "#27ae60";
  if (lowerFileName.endsWith(".ppt") || lowerFileName.endsWith(".pptx"))
    return "#f39c12";
  if (
    lowerFileName.endsWith(".xml") ||
    lowerFileName.endsWith(".html") ||
    lowerFileName.endsWith(".css") ||
    lowerFileName.endsWith(".js") ||
    lowerFileName.endsWith(".json")
  )
    return "#fd7e14";
  if (
    lowerFileName.endsWith(".zip") ||
    lowerFileName.endsWith(".rar") ||
    lowerFileName.endsWith(".7z")
  )
    return "#8e44ad";
  if (lowerFileName.match(/\.(jpg|jpeg|png|gif|bmp|svg|webp)$/))
    return "#e67e22";
  if (lowerFileName.match(/\.(mp3|wav|ogg|m4a|aac|flac)$/)) return "#9b59b6";
  if (lowerFileName.match(/\.(mp4|avi|mkv|mov|wmv|flv|webm)$/))
    return "#e91e63";

  // Fallback to MIME type
  if (lowerFileType.includes("pdf")) return "#e74c3c";
  if (
    lowerFileType.includes("csv") ||
    lowerFileType.includes("comma-separated")
  )
    return "#17a2b8"; // Cyan blue for CSV
  if (lowerFileType.includes("text") || lowerFileType.includes("plain"))
    return "#6c757d";
  if (
    lowerFileType.includes("word") ||
    lowerFileType.includes("document") ||
    lowerFileType.includes("msword")
  )
    return "#2980b9";
  if (
    lowerFileType.includes("excel") ||
    lowerFileType.includes("sheet") ||
    lowerFileType.includes("spreadsheet")
  )
    return "#27ae60";
  if (
    lowerFileType.includes("powerpoint") ||
    lowerFileType.includes("presentation")
  )
    return "#f39c12";
  if (
    lowerFileType.includes("xml") ||
    lowerFileType.includes("html") ||
    lowerFileType.includes("json") ||
    lowerFileType.includes("javascript")
  )
    return "#fd7e14";
  if (
    lowerFileType.includes("zip") ||
    lowerFileType.includes("rar") ||
    lowerFileType.includes("archive")
  )
    return "#8e44ad";
  if (lowerFileType.includes("image")) return "#e67e22";
  if (lowerFileType.includes("audio")) return "#9b59b6";
  if (lowerFileType.includes("video")) return "#e91e63";

  return "#7f8c8d";
};
